"use client";

import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";

// Components
import { AuthDialog } from "@/components/ui/AuthDialog";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { VocabWarmUpDialog } from "@/components/VocabWarmUpDialog";
import { FeatureLimitDialog } from "@/components/ui/FeatureLimitDialog";
// Custom hooks
import { useEpisodeData } from "@/hooks/useEpisodeData";
// import { useProgressBar } from "@/hooks/useProgressBar";
import { useUser } from "@/lib/context/user-context";
import { useUserSettings } from "@/contexts/user-settings-context";
import { useFavorites } from "@/hooks/useFavorites";
import { useUsage } from "@/contexts/usage-context";
import { AddToPlaylistDialog } from "@/components/playlist/AddToPlaylistDialog";
import { ExportEpisodeDialog } from "@/components/export/ExportEpisodeDialog";
import { useEpisodePlayer } from "@/hooks/useEpisodePlayer";
import { useEpisodeHistory } from "@/hooks/useEpisodeHistory";
import { useIsMobile } from "@/hooks/use-mobile";

// Episode components
import { EpisodeHeader } from "@/components/episode/EpisodeHeader";
import { VideoPlayerSection } from "@/components/episode/VideoPlayerSection";
import { FloatingActionButtons } from "@/components/episode/FloatingActionButtons";
import { ImmersiveFloatingActionButtons } from "@/components/episode/ImmersiveFloatingActionButtons";
import { MobileImmersiveFloatingButtons } from "@/components/episode/MobileImmersiveFloatingButtons";
import { ContentRenderer } from "@/components/episode/ContentRenderer";
import { TranscriptSectionSkeleton } from "@/components/episode/TranscriptSectionSkeleton";
import { MobileImmersiveContent } from "@/components/episode/MobileImmersiveContent";
import type { ActiveVocabularyTermData } from "@/components/episode/ImmersiveVocabularyCards";

// Libraries
import { getPrompts } from "@/lib/constants";
import { EPISODE_CONSTANTS } from "@/lib/constants/episode";
import { createVocabularyTermKey } from "@/lib/vocabulary-term-key";
import { vocabularyLevelManager } from "@/lib/vocabularyLevel/manager";
import { toast } from "sonner";
import { supabase } from '@/core/supabase';
// import { Button } from "@/components/ui/button";

// Memory monitoring (development only)
if (process.env.NODE_ENV === 'development') {
  import("@/utils/memory-monitor");
  import("@/utils/memory-profiler");
}

// Add type for tab values
type TabType = typeof EPISODE_CONSTANTS.TABS[keyof typeof EPISODE_CONSTANTS.TABS];

export default function EpisodePage({ 
  params, 
  searchParams 
}: { 
  params: { id: string };
  searchParams?: { t?: string };
}) {
  // Memory profiling in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const profiler = (window as any).memoryProfiler;
      if (profiler) {
        profiler.mark(`episode-${params.id}-page-mount`);
        profiler.startMonitoring(10000); // 每10秒监控一次
        
        return () => {
          profiler.mark(`episode-${params.id}-page-unmount`);
          profiler.measure(`episode-${params.id}-page-mount`, `episode-${params.id}-page-unmount`);
        };
      }
    }
  }, [params.id]);

  // Use custom hooks
  const {
    content,
    setContent,
    groupedSentences,
    setGroupedSentences,
    summary,
    setSummary,
    mindmapData,
    setMindmapData,
    vocabulary,
    setVocabulary,
    cleanReadData,
    setCleanReadData,
    learningSidebarData,
    setLearningSidebarData,
    error,
    loadingProgress,
    clearDataCache,
    clearGlobalCache,
    loadContent,
    accessInfo,
    generationRecords,
    setGenerationRecords,
    allowBasicPlanLite,
  } = useEpisodeData(params.id);

  // 🔍 Debug effect to track generationRecords changes in main page
  useEffect(() => {
    console.log('🔄 generationRecords changed in main page:', generationRecords);
  }, [generationRecords]);

  // Handle transcript update after generation
  const handleTranscriptUpdate = useCallback(async (transcript: any[], audioUrl?: string) => {
    // 🔍 调试：检查接收到的参数
    console.log('handleTranscriptUpdate called with:', {
      hasTranscript: !!transcript,
      hasAudioUrl: !!audioUrl,
      audioUrl: audioUrl,
      hasContent: !!content
    });
    
    // Update local state immediately with the new transcript
    setGroupedSentences(transcript);
    
    // 🔥 更新generation records，标记transcript已生成
    // 如果generationRecords为null，创建一个新的
    const updatedGenerationRecords = generationRecords || {
      transcript: false,
      summary: false,
      vocabulary: false,
      mindmap: false,
      cleanread: false
    };
    
    console.log('🔄 handleTranscriptUpdate - BEFORE setGenerationRecords:', {
      current: generationRecords,
      updated: updatedGenerationRecords,
      willSet: { ...updatedGenerationRecords, transcript: true }
    });
    
    setGenerationRecords({
      ...updatedGenerationRecords,
      transcript: true
    });
    
    console.log('✅ handleTranscriptUpdate - AFTER setGenerationRecords call');
    
    // 🔥 更新content和audioUrl
    if (audioUrl && content) {
      console.log('🎯 Got audioUrl from transcript generation:', audioUrl);
      
      // 更新content对象包含新的audioUrl
      const updatedContent = {
        ...content,
        content: {
          ...content.content,
          audioUrl: audioUrl,  // 添加audioUrl到content
          content: {
            ...content.content.content,
            transcript: transcript,
          },
        },
      };
      
      // 更新content状态
      setContent(updatedContent);
    }
    
    // 🔥 无论如何都清除缓存，确保下次加载时获取最新的generation records
    clearDataCache();
    clearGlobalCache();
    
    // 🔥 可选：如果需要立即更新所有数据（包括generation records）
    // 只在没有audioUrl时重新加载，避免重复加载
    if (!audioUrl) {
      console.log('📡 Reloading episode data to sync generation records...');
      await loadContent();
      console.log('✅ Episode data reloaded with latest generation records');
    }
  }, [content, setContent, setGroupedSentences, clearDataCache, clearGlobalCache, loadContent]);

  // 解析URL时间参数
  const initialTime = searchParams?.t ? parseInt(searchParams.t, 10) : undefined;
  
  // 🔥 合并 generationRecords 到 content，确保播放器可以访问
  const contentWithGenerationRecords = useMemo(() => {
    if (!content) return null;
    return {
      ...content,
      generationRecords: generationRecords || content.generationRecords,
    };
  }, [content, generationRecords]);
  
  // 使用全局播放器
  const globalPlayer = useEpisodePlayer(contentWithGenerationRecords, cleanReadData, initialTime);
  const episodeShareId = content?.shareId || content?.content?.shareId || "";
  
  // 处理YouTube播放器的时间同步（HTML5播放器已在PlayerContext中处理）
  const hasSetInitialTimeRef = useRef(false);
  const activeSegmentRef = useRef<{ start: number | null; end: number | null }>({
    start: null,
    end: null,
  });
  const lastResumeEpisodeRef = useRef<string | null>(null);
  useEffect(() => {
    // 只在第一次设置时间，避免重复设置
    if (initialTime && globalPlayer.ytPlayer && !hasSetInitialTimeRef.current) {
      hasSetInitialTimeRef.current = true;
      // 延迟一点确保YouTube播放器完全加载
      const timer = setTimeout(() => {
        console.log('📍 Setting YouTube initial time once:', initialTime);
        globalPlayer.seekTo(initialTime);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [initialTime, globalPlayer.ytPlayer, globalPlayer.seekTo]);
  
  // 保留本地的 YouTube 事件处理（用于同步到全局状态）
  const handleYouTubeReady = (player: any) => {
    globalPlayer.setYtPlayer(player);
  };

  const handleTimeUpdate = (time: number) => {
    // Track progress for history regardless of video type
    if (time > 0 && youtubeDuration) {
      trackProgress(time, youtubeDuration);
    }
    
    // 检查当前内容类型
    const origin = content?.content?.origin || content?.origin;
    const isYouTubeVideo = origin === 'video' || 
      (content?.content?.videoUrl && 
       (content.content.videoUrl.includes('youtube.com') || 
        content.content.videoUrl.includes('youtu.be')));
    
    const rawVideoUrl = content?.content?.videoUrl ?? content?.videoUrl ?? '';
    const isBlobVideo = typeof rawVideoUrl === 'string' && (
      rawVideoUrl.startsWith('blob:') ||
      rawVideoUrl.includes('blob.vercel-storage.com')
    );
    
    // 对于YouTube视频、blob视频或播客内容，接受时间更新
    if (isYouTubeVideo || isBlobVideo || origin === 'podcast') {
      // console.log('🕐 Episodes page calling globalPlayer.updateTime:', time, 'for origin:', origin);
      globalPlayer.updateTime(time);
      // console.log('🕐 Episodes page after updateTime, current globalPlayer.currentTime:', globalPlayer.currentTime);
      return;
    }
    
    // 对于纯音频内容，忽略VideoPlayer的时间更新（使用HTML5音频播放器的时间）
  };

  const handleDurationChange = (duration: number) => {
    console.log('📏 Episode page received duration update', {
      duration,
      previousDuration: youtubeDuration,
      contentOrigin: content?.content?.origin || content?.origin,
      videoUrl: content?.content?.videoUrl,
    });
    globalPlayer.updateDuration(duration);
  };
  
  // 从全局播放器获取需要的属性和方法
  const {
    currentTime: youtubeTime,
    duration: youtubeDuration,
    isPlaying,
    // playbackSpeed,
    ytPlayer,
    formatTime,
    play,
    pause,
    seekTo,
    handleMediaEnded,
    // setPlaybackSpeed,
    // skipBackward: handleSkipBackward,
    // skipForward: handleSkipForward,
  } = globalPlayer;
  
  // 格式化持续时间
  const formatDuration = (duration: number) => {
    return formatTime(duration);
  };
  
  // 播放/暂停处理 - 用于外部控制（如按钮点击）
  // const handlePlayPause = () => {
  //   if (isPlaying) {
  //     pause();
  //   } else {
  //     play();
  //   }
  // };
  
  // VideoPlayer 内部播放状态变化的处理
  // 这个函数不会再次调用 play/pause，避免循环
  const handleVideoPlayerStateChange = (isPlaying: boolean) => {
    // 检查当前内容类型
    const origin = content?.content?.origin || content?.origin;
    const isYouTubeVideo = origin === 'video' || 
      (content?.content?.videoUrl && 
       (content.content.videoUrl.includes('youtube.com') || 
        content.content.videoUrl.includes('youtu.be')));
    
    const isBlobVideo = content?.content?.videoUrl && content.content.videoUrl.includes('blob.vercel-storage.com');
    
    // 对于YouTube视频，仅更新全局播放状态，不触发播放器控制
    if (isYouTubeVideo) {
      globalPlayer.updatePlayingState(isPlaying);
      
      if (!isPlaying && globalPlayer.currentTime > 0 && youtubeDuration) {
        saveProgressImmediately(globalPlayer.currentTime, youtubeDuration, 'youtube_pause');
      }
    }
    
    // 对于blob视频，也要保存暂停时的进度
    if (isBlobVideo) {
      globalPlayer.updatePlayingState(isPlaying);
      
      if (!isPlaying && globalPlayer.currentTime > 0 && youtubeDuration) {
        saveProgressImmediately(globalPlayer.currentTime, youtubeDuration, 'blob_pause');
      }
    }
  };
  
  // 设置时间（用于进度条拖动）
  // const setYoutubeTime = (time: number) => {
  //   seekTo(time);
    
  //   // 🔥 新增：Podverse风格事件驱动保存 - 跳转时保存进度
  //   if (time > 0 && youtubeDuration) {
  //     console.log('🎯 YouTube video seeked, saving progress immediately');
  //     saveProgressImmediately(time, youtubeDuration, 'youtube_seek');
  //   }
  // };
  
  // 设置播放状态（用于其他组件）
  const setIsPlaying = (playing: boolean) => {
    if (playing) {
      play();
    } else {
      pause();
    }
  };

  // const {
  //   progressBarRef,
  //   handleMouseDown,
  //   handleTouchStart,
  //   isDragging
  // } = useProgressBar(youtubeDuration, ytPlayer, setYoutubeTime);

  // Local state
  const [activeTab, setActiveTab] = useState<TabType>(EPISODE_CONSTANTS.TABS.TRANSCRIPT);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  // Remove local loading state, use loadingProgress.data from useEpisodeData instead
  const [isVocabularyLoading, setIsVocabularyLoading] = useState(false);
  const [isCleanReadLoading, setIsCleanReadLoading] = useState(false);
  const [wordHighlightEnabled, setWordHighlightEnabled] = useState(false);
  const [wordHighlightLoading, setWordHighlightLoading] = useState(false);
  const [isWordCheckerReady, setIsWordCheckerReady] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);
  const [practiceModeType, setPracticeModeType] = useState<"vocabulary" | "toefl" | "shadowing">(EPISODE_CONSTANTS.PRACTICE_MODES.VOCABULARY);
  const [vocabWarmUpOpen, setVocabWarmUpOpen] = useState(false);
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isImmersiveMode, setIsImmersiveMode] = useState(false);
  const [activeSubtitleId, setActiveSubtitleId] = useState<string | null>(null);
  const [activeSubtitleText, setActiveSubtitleText] = useState<string>('');
  const [activeSubtitleTranslation, setActiveSubtitleTranslation] = useState<string>('');
  const [activeSubtitleTimestamp, setActiveSubtitleTimestamp] = useState<[number, number] | null>(null);
  const latestActiveSubtitle = useRef({
    id: '' as string | null,
    text: '' as string,
    translation: '' as string,
    timestamp: null as [number, number] | null,
  });
  const [activeVocabularyTerms, setActiveVocabularyTerms] = useState<ActiveVocabularyTermData[]>([]);
  const [hiddenVocabularyTermMap, setHiddenVocabularyTermMap] = useState<Record<string, true>>({});
  const [filteredVocabularyTermMap, setFilteredVocabularyTermMap] = useState<Record<string, true>>({});
  const activeVocabularyTermLabels = useMemo(() => {
    return activeVocabularyTerms
      .map((entry) => {
        const term = entry.term || "";
        const trimmed = term.trim();
        return trimmed.length > 0 ? trimmed : term;
      })
      .filter((term): term is string => Boolean(term));
  }, [activeVocabularyTerms]);
  const hiddenVocabularyTermKeys = useMemo(() => {
    const combined = new Set<string>();
    Object.keys(hiddenVocabularyTermMap).forEach((key) => combined.add(key));
    Object.keys(filteredVocabularyTermMap).forEach((key) => combined.add(key));
    return Array.from(combined);
  }, [hiddenVocabularyTermMap, filteredVocabularyTermMap]);
  const [showSubtitlesInImmersive, setShowSubtitlesInImmersive] = useState(true);
  const [showTranslationInImmersive, setShowTranslationInImmersive] = useState(false);
  const [revealAllTranslationsInImmersive, setRevealAllTranslationsInImmersive] = useState(false);
  const [mobileImmersiveMode, setMobileImmersiveMode] = useState(false);
  const { userSettings } = useUserSettings();
  const isMobileViewport = useIsMobile();

  const getUserDictionarySettings = useCallback(() => {
    const dictionaryFile = userSettings?.dictionarySetting || null;
    const dictionaryType =
      userSettings?.vocabularyDictionary || (dictionaryFile ? 'TOEFL' : null);
    return { dictionaryType, dictionaryFile };
  }, [userSettings]);

  const ensurePhraseDictionary = useCallback(async () => {
    if (isWordCheckerReady) {
      return;
    }

    const { dictionaryType, dictionaryFile } = getUserDictionarySettings();
    if (!dictionaryFile) {
      return;
    }

    try {
      const currentFile = vocabularyLevelManager.getCurrentDictionaryFile();
      if (
        !vocabularyLevelManager.isInitialized() ||
        currentFile !== dictionaryFile
      ) {
        await vocabularyLevelManager.ensureInitialized(
          dictionaryType || 'TOEFL',
          dictionaryFile
        );
      }
      setIsWordCheckerReady(true);
    } catch (error) {
      console.error('Failed to initialize dictionary for Phrase tab:', error);
    }
  }, [getUserDictionarySettings, isWordCheckerReady]);

  // ✅ PERFORMANCE OPTIMIZATION: Trigger vocabulary preloading when practice mode is enabled
  useEffect(() => {
    if (practiceMode && 
        practiceModeType === 'toefl' && 
        groupedSentences && 
        groupedSentences.length > 0 &&
        isWordCheckerReady) {
      
      // This will be handled by the GroupedTranscriptEntry components' preloader
    }
  }, [practiceMode, practiceModeType, groupedSentences, isWordCheckerReady]);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [isVideoMinimized, setIsVideoMinimized] = useState(false);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [summaryShowTranslation, setSummaryShowTranslation] = useState(false);
  const hasAppliedTranslationScrollRef = useRef(false);
  const activeTabRef = useRef(activeTab);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      isMobileViewport &&
      (activeTab === EPISODE_CONSTANTS.TABS.VOCABULARY || activeTab === EPISODE_CONSTANTS.TABS.PHRASE)
    ) {
      // Auto-collapse when entering vocabulary on mobile, but keep user-triggered restores intact
      setIsVideoMinimized((prev) => (prev ? prev : true));
    }
  }, [isMobileViewport, activeTab]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== EPISODE_CONSTANTS.TABS.PHRASE) {
      return;
    }

    void ensurePhraseDictionary();
  }, [activeTab, ensurePhraseDictionary]);

  useEffect(() => {
    if (activeTab !== EPISODE_CONSTANTS.TABS.PHRASE) {
      return;
    }

    if (isWordCheckerReady && !wordHighlightEnabled) {
      setWordHighlightEnabled(true);
    }
  }, [activeTab, isWordCheckerReady, wordHighlightEnabled, setWordHighlightEnabled]);

  const scrollToCurrentTranscript = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Only scroll when transcript tab is active
    if (activeTabRef.current !== EPISODE_CONSTANTS.TABS.TRANSCRIPT) {
      return;
    }

    const activeTranscript = document.querySelector<HTMLElement>(
      '.transcript-content-container [data-active="true"]'
    );

    if (activeTranscript) {
      const elementTop = activeTranscript.getBoundingClientRect().top;
      const offsetTop = window.scrollY + elementTop - EPISODE_CONSTANTS.STICKY_HEADERS_HEIGHT;

      window.scrollTo({
        top: Math.max(offsetTop, 0),
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    if (!hasAppliedTranslationScrollRef.current) {
      hasAppliedTranslationScrollRef.current = true;
      return;
    }

    let animationFrameId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleScroll = () => {
      scrollToCurrentTranscript();
      timeoutId = setTimeout(() => {
        scrollToCurrentTranscript();
      }, 350);
    };

    if (typeof window !== 'undefined') {
      animationFrameId = requestAnimationFrame(scheduleScroll);
    }

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [showTranslation, scrollToCurrentTranscript]);

  const { user, isLoading: isUserLoading } = useUser();
  const { toggleFavorite, isFavorited } = useFavorites();
  const { usageInfo, loading: usageLoading } = useUsage();
  const metadataTwoBrokeGirlsAccess = useMemo(() => {
    const flag = user?.user_metadata?.twoBrokeGirlsAccess === true;
    if (!flag) return false;

    const expiresAt = user?.user_metadata?.twoBrokeGirlsAccessExpiresAt;
    if (!expiresAt) return true;

    const expiresDate = new Date(expiresAt);
    if (Number.isNaN(expiresDate.getTime())) {
      return true;
    }

    return expiresDate > new Date();
  }, [user?.user_metadata?.twoBrokeGirlsAccess, user?.user_metadata?.twoBrokeGirlsAccessExpiresAt]);

  const isTwoBrokeGirlsEpisode = useMemo(() => {
    const resolvedId =
      params.id ||
      content?.content?.shareId ||
      content?.shareId;

    if (!resolvedId) return false;

    return resolvedId.toString().toUpperCase().startsWith('PCJMS0');
  }, [params.id, content?.content?.shareId, content?.shareId]);

  const hasTwoBrokeGirlsAccess = useMemo(() => {
    if (!isTwoBrokeGirlsEpisode) {
      return false;
    }

    return metadataTwoBrokeGirlsAccess;
  }, [isTwoBrokeGirlsEpisode, metadataTwoBrokeGirlsAccess]);
  
  // Session state for authentication
  const [session, setSession] = useState<any>(null);
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(false);
  const [premiumPromptFeature, setPremiumPromptFeature] = useState<string>('');
  
  // Get thumbnail URL with multiple fallback options
  const getThumbnailUrl = useCallback(() => {
    if (!content) return undefined;
    
    const episode = content.content || content;
    
    // Try different thumbnail fields in order of preference
    const thumbnailUrl = episode.thumbnail || 
                        episode.videoThumbnail || 
                        episode.ownerAvatarUrl ||
                        episode.avatar ||
                        undefined;
    
    return thumbnailUrl;
  }, [content]);

  // Initialize episode history tracking
  const { trackProgress, markCompleted, getLastPosition, saveProgressImmediately } = useEpisodeHistory(
    params.id,
    content?.content?.title || content?.title || 'Unknown Episode',
    content?.content?.author || 'Unknown Podcast',
    getThumbnailUrl(),
    content?.content?.description || content?.description || '',  // 🔥 新增：传递description
    content?.content?.ownerAvatarUrl || content?.ownerAvatarUrl || undefined,
    youtubeDuration
  );

  // Use ref to store latest content for callbacks
  const contentRef = useRef(content);
  contentRef.current = content;

  // 🔥 新增：Podverse风格事件驱动保存 - 页面关闭前保存进度
  const youtubeTimeRef = useRef(youtubeTime);
  useEffect(() => {
    youtubeTimeRef.current = youtubeTime;
  }, [youtubeTime]);

  const youtubeDurationRef = useRef(youtubeDuration);
  useEffect(() => {
    youtubeDurationRef.current = youtubeDuration;
  }, [youtubeDuration]);

  const contentOriginRef = useRef<string | undefined>(content?.content?.origin || content?.origin);
  useEffect(() => {
    contentOriginRef.current = content?.content?.origin || content?.origin;
  }, [content]);

  const contentVideoUrlRef = useRef<string | undefined>(content?.content?.videoUrl);
  useEffect(() => {
    contentVideoUrlRef.current = content?.content?.videoUrl;
  }, [content]);

  const saveProgressRef = useRef(saveProgressImmediately);
  useEffect(() => {
    saveProgressRef.current = saveProgressImmediately;
  }, [saveProgressImmediately]);

  const lastGroupIndexRef = useRef<number>(-1);
  const resumeTimeoutRef = useRef<number | null>(null);
  const resumeScheduledFromRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      resumeTimeoutRef.current !== null &&
      resumeScheduledFromRef.current !== null &&
      Math.abs(youtubeTime - resumeScheduledFromRef.current) > 5
    ) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
      resumeScheduledFromRef.current = null;
    }
  }, [youtubeTime]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const origin = contentOriginRef.current;
      const videoUrl = contentVideoUrlRef.current;
      const isYouTubeVideo = origin === 'video' ||
        (videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')));

      if (isYouTubeVideo && youtubeTimeRef.current > 0 && youtubeDurationRef.current) {
        console.log('🎯 Page unload - saving YouTube progress');
        saveProgressRef.current(youtubeTimeRef.current, youtubeDurationRef.current, 'youtube_page_unload');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Detect podcast origin and automatically enable Minimize mode
  useEffect(() => {
    if (!content) return;
    
    const origin = content?.content?.origin;
    
    if (origin === 'podcast') {
      console.log('📺 Podcast episode detected - enabling Minimize mode in VideoPlayerSection');
      if (!isVideoMinimized) {
        setIsVideoMinimized(true);
      }
    }
  }, [content, isVideoMinimized]);

  // 🔥 获取session用于认证
  useEffect(() => {
    if (isUserLoading) return;

    const fetchSession = async () => {
      try {
        if (!user) {
          setSession(null);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
      } catch (error) {
        console.error('Failed to get session:', error);
        setSession(null);
      }
    };

    fetchSession();
  }, [user, isUserLoading]);

  // Load user's last position when content is available
  useEffect(() => {
    if (!content || !user || !youtubeDuration) return;

    const currentEpisodeId =
      content?.shareId ||
      content?.content?.shareId ||
      content?.content?.content?.id ||
      null;

    const resumeKey = currentEpisodeId ? String(currentEpisodeId) : null;

    if (!searchParams?.t && resumeKey && lastResumeEpisodeRef.current === resumeKey) {
      return;
    }

    const loadLastPosition = async () => {
      try {
        const lastPosition = await getLastPosition();
        
        // If user has a saved position and it's more than 10 seconds in
        if (lastPosition > 10) {
          console.log(`📚 Resuming from last position: ${lastPosition}s`);
          
          // Check if there's a URL parameter for time
          const urlTime = searchParams?.t ? parseFloat(searchParams.t) : null;
          
          // Use URL time if provided, otherwise use last saved position
          let seekTime = urlTime ?? lastPosition;

          // Clamp resume position to current media duration when we reuse history from
          // an older/longer asset that shared the same episode id.
          if (!urlTime && youtubeDuration && youtubeDuration > 0 && seekTime > youtubeDuration) {
            const clampedTime = Math.max(0, youtubeDuration - 2); // leave a small buffer
            console.log('⏱️ Resume position exceeds duration, clamping', {
              original: seekTime,
              duration: youtubeDuration,
              clampedTime,
            });
            seekTime = clampedTime;
          }
          
          // Seek to the saved position after a brief delay to ensure player is ready
          if (resumeTimeoutRef.current !== null) {
            clearTimeout(resumeTimeoutRef.current);
            resumeTimeoutRef.current = null;
          }

          const scheduledFrom =
            globalPlayer.currentTime ??
            youtubeTime ??
            0;
          resumeScheduledFromRef.current = scheduledFrom;
          resumeTimeoutRef.current = window.setTimeout(() => {
            resumeTimeoutRef.current = null;
            const currentAtFire = globalPlayer.currentTime ?? 0;
            const scheduledStart = resumeScheduledFromRef.current ?? currentAtFire;

            if (Math.abs(currentAtFire - scheduledStart) > 5) {
              resumeScheduledFromRef.current = null;
              return;
            }

            seekTo(seekTime);
            resumeScheduledFromRef.current = null;
          }, 1000);
          if (!searchParams?.t && resumeKey) {
            lastResumeEpisodeRef.current = resumeKey;
          }
        } else {
          if (!searchParams?.t) {
            console.log('🧭 No saved history, resetting player time to 0');
            seekTo(0);
            globalPlayer.updateTime(0);
            if (resumeKey) {
              lastResumeEpisodeRef.current = resumeKey;
            }
          }
        }
      } catch (error) {
        console.error('Error loading last position:', error);
      }
    };

    loadLastPosition();

    return () => {
      if (resumeTimeoutRef.current !== null) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      resumeScheduledFromRef.current = null;
      if (!searchParams?.t && resumeKey) {
        lastResumeEpisodeRef.current = resumeKey;
      }
    };
  }, [content, user, youtubeDuration, getLastPosition, seekTo, searchParams?.t, globalPlayer.updateTime]);

  // Handle login
  const handleLogin = useCallback(() => {
    setAuthDialogOpen(true);
  }, []);

  // Handle star click
  const handleStarClick = useCallback(async () => {
    // Get the most current content from ref to avoid stale closure
    const currentContent = contentRef.current;
    
    if (!currentContent) {
      console.warn('Content not loaded yet, cannot toggle favorite');
      return;
    }
    
    // Try both possible locations for shareId
    const shareId = currentContent?.shareId || currentContent?.content?.shareId;
    
    if (!shareId) {
      console.warn('No shareId found in content, cannot toggle favorite');
      return;
    }
    
    await toggleFavorite(shareId);
  }, [toggleFavorite]);

  const handleAddToListClick = useCallback(() => {
    setShowAddToPlaylist(true);
  }, []);

  const handleShareClick = useCallback(() => {
    setShowExportDialog(true);
  }, []);

  // Handle toggle minimize
  const handleToggleMinimize = useCallback(() => {
    setIsVideoMinimized((prev) => !prev);
  }, []);

  // Handle focus mode change
  const handleFocusModeChange = useCallback((focusMode: boolean) => {
    setIsFocusMode(focusMode);
    // Auto-minimize video when entering focus mode
    if (focusMode && !isVideoMinimized) {
      setIsVideoMinimized(true);
    }
    // Auto-maximize video when exiting focus mode (optional)
    // if (!focusMode && isVideoMinimized) {
    //   setIsVideoMinimized(false);
    // }
  }, [isVideoMinimized]);

  // Handle summary translation state change
  const handleSummaryTranslationStateChange = useCallback((showTranslation: boolean) => {
    setSummaryShowTranslation(showTranslation);
  }, []);

  // Handle immersive mode change
  const handleImmersiveModeChange = useCallback((immersiveMode: boolean) => {
    setIsImmersiveMode(immersiveMode);
    // When entering immersive mode, ensure video is not minimized
    if (immersiveMode && isVideoMinimized) {
      setIsVideoMinimized(false);
    }
  }, [isVideoMinimized]);

  // Handle mobile immersive mode change
  const handleMobileImmersiveModeChange = useCallback((isEnabled: boolean) => {
    setMobileImmersiveMode(isEnabled);
  }, []);

  // 🚀 性能优化：节流字幕更新
  const handleActiveSubtitleChange = useCallback((
    subtitleId: string | null,
    subtitleText?: string,
    subtitleTranslation?: string,
    subtitleTimestamp?: [number, number]
  ) => {
    // Ensure translation is always a string
    const safeTranslation = typeof subtitleTranslation === 'object' 
      ? '' // Should have been processed before calling this, but safety fallback
      : (subtitleTranslation || '');

    const nextState = {
      id: subtitleId,
      text: subtitleText || '',
      translation: safeTranslation,
      timestamp: subtitleTimestamp ?? null,
    };
    if (nextState.text || nextState.translation) {
      latestActiveSubtitle.current = nextState;
    }
    setActiveSubtitleId(nextState.id);
    setActiveSubtitleText(nextState.text);
    setActiveSubtitleTranslation(nextState.translation);
    setActiveSubtitleTimestamp(nextState.timestamp);
  }, []);

  const restoreActiveSubtitle = useCallback(() => {
    const snapshot = latestActiveSubtitle.current;
    if (snapshot.id || snapshot.text || snapshot.translation) {
      setActiveSubtitleId(snapshot.id);
      setActiveSubtitleText(snapshot.text);
      setActiveSubtitleTranslation(snapshot.translation);
      setActiveSubtitleTimestamp(snapshot.timestamp);
    }
  }, []);

  // Mobile immersive mode subtitle tracking with throttling for performance
  useEffect(() => {
    if (!mobileImmersiveMode) {
      lastGroupIndexRef.current = -1;
      return;
    }

    if (!groupedSentences || groupedSentences.length === 0) {
      lastGroupIndexRef.current = -1;
      handleActiveSubtitleChange(null, '', '');
      return;
    }

    if (lastGroupIndexRef.current >= groupedSentences.length) {
      lastGroupIndexRef.current = -1;
    }

    // Throttle updates to every 250ms for better performance
    const throttledTime = Math.floor(youtubeTime * 4) / 4;
    const tolerance = 0.01;

    const isTimeInRange = (time: number, start: number, end: number) => {
      return time >= start - tolerance && time <= end + tolerance;
    };

    const groups = groupedSentences;

    const withinGroup = (index: number) =>
      index >= 0 &&
      index < groups.length &&
      isTimeInRange(throttledTime, groups[index].timestamp[0], groups[index].timestamp[1]);

    let groupIndex = lastGroupIndexRef.current;

    if (!withinGroup(groupIndex)) {
      // Try immediate neighbors before falling back to binary search
      if (withinGroup(groupIndex + 1)) {
        groupIndex = groupIndex + 1;
      } else if (withinGroup(groupIndex - 1)) {
        groupIndex = groupIndex - 1;
      } else {
        let left = 0;
        let right = groups.length - 1;
        let foundIndex = -1;

        while (left <= right) {
          const mid = Math.floor((left + right) / 2);
          const [start, end] = groups[mid].timestamp;

          if (isTimeInRange(throttledTime, start, end)) {
            foundIndex = mid;
            break;
          }

          if (throttledTime < start) {
            right = mid - 1;
          } else {
            left = mid + 1;
          }
        }

        groupIndex = foundIndex;
      }
    }

    if (!withinGroup(groupIndex)) {
      lastGroupIndexRef.current = -1;
      handleActiveSubtitleChange(null, '', '');
      return;
    }

    lastGroupIndexRef.current = groupIndex;

    const activeGroup = groups[groupIndex];
    let currentChunk = activeGroup.chunks?.find((chunk: any) =>
      isTimeInRange(throttledTime, chunk.timestamp[0], chunk.timestamp[1])
    );

    if (!currentChunk && (!activeGroup.chunks || activeGroup.chunks.length === 0)) {
      currentChunk = activeGroup;
    }

    if (currentChunk) {
      const subtitleId = `mobile-${groupIndex}-${currentChunk.timestamp[0]}`;
      const subtitleText = currentChunk.text?.trim() || '';
      
      let subtitleTranslation = '';
      const rawTranslation = currentChunk.translation;
      if (typeof rawTranslation === 'string') {
        subtitleTranslation = rawTranslation.trim();
      } else if (typeof rawTranslation === 'object' && rawTranslation !== null) {
        const lang = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
        subtitleTranslation = (rawTranslation[lang] || rawTranslation['zh'] || Object.values(rawTranslation)[0] || '').trim();
      }

      if (subtitleText || subtitleTranslation) {
        handleActiveSubtitleChange(
          subtitleId,
          subtitleText,
          subtitleTranslation,
          currentChunk.timestamp as [number, number]
        );
        return;
      }
    }

    handleActiveSubtitleChange(null, '', '');
  }, [
    mobileImmersiveMode,
    groupedSentences,
    youtubeTime,
    handleActiveSubtitleChange
  ]);


  const handleVocabularyCardSwipe = useCallback(
    (
      { term, scenarioName }: { term: string; scenarioName?: string },
      direction: "left" | "right"
    ) => {
      const key = createVocabularyTermKey(term, scenarioName);
      if (!key) {
        return;
      }

      setHiddenVocabularyTermMap((previous) => {
        if (direction === "left") {
          if (previous[key]) {
            return previous;
          }
          return { ...previous, [key]: true };
        }

        if (direction === "right") {
          if (!previous[key]) {
            return previous;
          }
          const { [key]: _removed, ...rest } = previous;
          return rest;
        }

        return previous;
      });
    },
    []
  );

  const handleFilterHiddenTermsChange = useCallback((keys: string[]) => {
    setFilteredVocabularyTermMap((previous) => {
      if (keys.length === 0) {
        if (Object.keys(previous).length === 0) {
          return previous;
        }
        return {};
      }

      const next: Record<string, true> = {};
      keys.forEach((key) => {
        next[key] = true;
      });

      const sameSize = Object.keys(previous).length === keys.length;
      if (sameSize) {
        let identical = true;
        for (let index = 0; index < keys.length; index += 1) {
          const key = keys[index];
          if (!previous[key]) {
            identical = false;
            break;
          }
        }
        if (identical) {
          return previous;
        }
      }

      return next;
    });
  }, []);

  // 处理active vocabulary terms更新
  const handleActiveVocabularyTermsChange = useCallback(
    (terms: ActiveVocabularyTermData[]) => {
      if (!terms || terms.length === 0) {
        setActiveVocabularyTerms([]);
        return;
      }

      const filtered = terms.filter((entry) => {
        const key = createVocabularyTermKey(entry.term, entry.scenarioName);
        if (!key) {
          return true;
        }

        return !(
          hiddenVocabularyTermMap[key] || filteredVocabularyTermMap[key]
        );
      });

      setActiveVocabularyTerms(filtered);
    },
    [hiddenVocabularyTermMap, filteredVocabularyTermMap]
  );

  // 为Safari添加媒体会话支持的meta标签
  useEffect(() => {
    if (content?.content) {
      // 动态更新页面title
      document.title = content.content.title || 'Episode';
      
      // 添加或更新Safari需要的meta标签
      const updateMetaTag = (name: string, content: string) => {
        let meta = document.querySelector(`meta[name="${name}"]`) || 
                  document.querySelector(`meta[property="${name}"]`);
        
        if (!meta) {
          meta = document.createElement('meta');
          if (name.startsWith('og:') || name.startsWith('twitter:')) {
            meta.setAttribute('property', name);
          } else {
            meta.setAttribute('name', name);
          }
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      // 更新Open Graph和Twitter Card meta标签（Safari可能会用到）
      updateMetaTag('og:title', content.content.title || 'Episode');
      updateMetaTag('og:description', content.content.description || 'Podcast Episode');
      updateMetaTag('og:image', content.content.thumbnail || '');
      updateMetaTag('og:type', 'video.other');
      updateMetaTag('og:video', content.content.videoUrl || '');
      
      updateMetaTag('twitter:card', 'player');
      updateMetaTag('twitter:title', content.content.title || 'Episode');
      updateMetaTag('twitter:description', content.content.description || 'Podcast Episode');
      updateMetaTag('twitter:image', content.content.thumbnail || '');
      
      // Apple特定的meta标签
      updateMetaTag('apple-mobile-web-app-capable', 'yes');

      const isDarkMode = document.documentElement.classList.contains('dark');
      updateMetaTag('theme-color', isDarkMode ? '#0b0b0b' : '#fefefe');
      updateMetaTag('apple-mobile-web-app-status-bar-style', isDarkMode ? 'black' : 'default');
      updateMetaTag('apple-mobile-web-app-title', content.content.title || 'Episode');

      const applyAppChromeTheme = (window as typeof window & { __applyAppChromeTheme?: () => void }).__applyAppChromeTheme;
      if (applyAppChromeTheme) {
        applyAppChromeTheme();
      }
      
      // 媒体会话相关
      // updateMetaTag('theme-color', '#d97757');
    }

    return () => {
      // 清理时恢复默认title
      document.title = 'Lingoflows';
    };
  }, [content]);


  // Add helper functions for common operations
  const updateContentField = useCallback((field: string, value: any) => {
      if (content) {
        // For learning sidebar, save at content level (same as cleanRead)
        if (field === 'learningSidebar') {
          const updatedContent = {
            ...content,
            content: {
              ...content.content,
              [field]: value,
            },
          };
          setContent(updatedContent);
          setLearningSidebarData(value); // Update local state
        } else {
          // For other fields, save at content.content level
          const updatedContent = {
            ...content,
            content: {
              ...content.content,
              content: {
                ...content.content.content,
                [field]: value,
              },
            },
          };
          setContent(updatedContent);
        }
      }
  }, [content, setContent, setLearningSidebarData]);

  // Generic Redis save function
  const saveToRedis = useCallback(async (endpoint: string, data: any, field: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`/api/episodes/${params.id}/${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ [field]: data }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save ${field}`);
      }

      updateContentField(field, data);
    } catch (error) {
      console.error(`Error saving ${field}:`, error);
      throw error; // Re-throw for caller to handle
    }
  }, [params.id, updateContentField]);

  // Save summary to Redis
  const saveSummaryToRedis = useCallback(async (summaryText: string) => {
    try {
      await saveToRedis("summary", summaryText, "summary");
    } catch (error) {
      console.error("Error saving summary:", error);
    }
  }, [saveToRedis]);

  // Check if transcript exists before generating content
  const checkTranscriptExists = useCallback(() => {
    const transcript = content?.content?.content?.transcript;
    if (!transcript || transcript.length === 0) {
      toast.info("Please generate transcript first before creating other content.", {
        duration: 4000,
      });
      return false;
    }
    return true;
  }, [content]);

  // Generate summary
  // 🔥 Premium功能权限检查辅助函数
  const checkPremiumFeature = useCallback((featureName: string) => {
    if (!user) {
      toast.error('Please login to use this feature');
      return false;
    }

    // 如果还在加载中或状态未确定，暂时阻止使用避免误操作
    if (usageInfo.isPremium === null || usageLoading) {
      toast.info('Loading user permissions...');
      return false;
    }

    const isBasicPlanLite = usageInfo.planType === 'basic_lite' || usageInfo.isBasicPlanLite === true;

    // Basic Plan Lite 特殊权限配置
    const basicPlanLiteAllowedFeatures = new Set(['Vocabulary']);

    if (isBasicPlanLite && basicPlanLiteAllowedFeatures.has(featureName)) {
      return true;
    }

    // 完全依赖API返回的最新数据，不信任客户端缓存
    if (usageInfo.isPremium === false) {
      setPremiumPromptFeature(featureName);
      setShowPremiumPrompt(true);
      return false;
    }

    return true;
  }, [user, usageInfo.isPremium, usageInfo.isBasicPlanLite, usageInfo.planType, usageLoading]);

  const generateSummary = useCallback(
    async (transcriptData: any) => {
      try {
        // 🔥 Premium功能检查
        if (!checkPremiumFeature('Summary')) return;
        
        setIsSummaryLoading(true)
        
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          toast.error('Authentication required');
          return;
        }
        
        // 🔥 检查是否已有summary内容
        if (content?.content?.content?.summary) {
          console.log(`✨ Episode already has summary, using fake generation effect`);
          
          // 模拟生成效果，逐字显示
          const existingSummary = content.content.content.summary;
          let displayedText = "";
          const words = existingSummary.split(' ');
          const wordsPerBatch = 5; // 每批显示5个词
          
          for (let i = 0; i < words.length; i += wordsPerBatch) {
            const batch = words.slice(i, i + wordsPerBatch).join(' ');
            displayedText += (displayedText ? ' ' : '') + batch;
            setSummary(displayedText);
            
            // 等待100ms显示下一批
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
          // 保存到Redis并更新生成记录
          await saveSummaryToRedis(existingSummary);
          setIsSummaryLoading(false);
          return;
        }
        
        const defaultPrompt = getPrompts("english")[0]

        const response = await fetch(`/api/episodes/${params.id}/summary`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            prompt: defaultPrompt.content,
            context: {
              transcript: transcriptData,
              metadata: {
                title: content?.content.title
              }
            }
          })
        });

        if (!response.ok || !response.body) {
          throw new Error("Failed to generate summary")
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let summaryText = ""
        let buffer = ""
        const updateInterval = 100
        let lastUpdateTime = Date.now()

        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            if (buffer.length > 0) {
              summaryText += buffer
              setSummary(summaryText)
            }
            // 在生成完成后保存到Redis
            await saveSummaryToRedis(summaryText)
            break
          }

          buffer += decoder.decode(value)
          const currentTime = Date.now()

          if (currentTime - lastUpdateTime >= updateInterval && buffer.length >= 50) {
            summaryText += buffer
            setSummary(summaryText)
            buffer = ""
            lastUpdateTime = currentTime
          }
        }
      } catch (error) {
        console.error("Error generating summary:", error)
        setSummary("Failed to generate summary. Please try again later.")
      } finally {
        setIsSummaryLoading(false)
      }
    },
    [content?.content.title, setSummary, saveSummaryToRedis]
  );

  // Save mindmap to Redis
  const saveMindmapToRedis = async (mindmapText: string) => {
    try {
      await saveToRedis("mindmap", mindmapText, "mindmap");
    } catch (error) {
      console.error("Error saving mindmap:", error);
    }
  };

  // Generate mindmap
  const [isMindmapLoading, setIsMindmapLoading] = useState(false);
  
  const generateMindmap = async () => {
    try {
      // 🔥 Premium功能检查
      if (!checkPremiumFeature('Mindmap')) return;
      
      setIsMindmapLoading(true)
      
      // Check if content is available
      if (!content || !content.content || !content.content.content || !content.content.content.transcript) {
        console.error("Content not available for mindmap generation");
        setIsMindmapLoading(false);
        return;
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      
      // 🔥 检查是否已有mindmap内容
      if (content?.content?.content?.mindmap) {
        console.log(`✨ Episode already has mindmap, using fake generation effect`);
        
        // 模拟生成效果，逐步显示
        const existingMindmap = content.content.content.mindmap;
        let displayedText = "";
        const lines = existingMindmap.split('\n');
        
        for (const line of lines) {
          displayedText += (displayedText ? '\n' : '') + line;
          setMindmapData(displayedText);
          
          // 等待150ms显示下一行
          await new Promise(resolve => setTimeout(resolve, 150));
        }
        
        // 保存到Redis并更新生成记录
        await saveMindmapToRedis(existingMindmap);
        setIsMindmapLoading(false);
        return;
      }
      if (!session?.access_token) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`/api/episodes/${params.id}/mindmap`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ content }),
      });

      if (!response.ok || !response.body) {
        throw new Error("Failed to generate mindmap");
      }

      const mindmapText = await response.text();
      setMindmapData(mindmapText);
      
      // 保存到 Redis
      await saveMindmapToRedis(mindmapText);
    } catch (error) {
      console.error("Error generating mindmap:", error)
    } finally {
      setIsMindmapLoading(false)
    }
  };

  // Generate clean read article
  const generateCleanRead = useCallback(
    async () => {
      try {
        // 🔥 Premium功能检查
        if (!checkPremiumFeature('Clean Read')) return;
        
        setIsCleanReadLoading(true);

        if (
          !content ||
          !content.content ||
          !content.content.content ||
          !content.content.content.transcript
        ) {
          console.error("Content not available for clean read generation");
          setIsCleanReadLoading(false);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          toast.error('Authentication required');
          return;
        }
        
        // 🔥 检查是否已有cleanRead内容
        if (content?.content?.content?.cleanRead) {
          console.log(`✨ Episode already has clean read, using fake generation effect`);
          
          // 模拟生成效果
          const existingCleanRead = content.content.content.cleanRead;
          
          // 等待8秒模拟生成过程
          await new Promise(resolve => setTimeout(resolve, 8000));
          
          setCleanReadData(existingCleanRead);
          
          // 保存到Redis并更新生成记录
          await saveToRedis("clean-read", existingCleanRead, "cleanRead");
          setIsCleanReadLoading(false);
          return;
        }

        const response = await fetch(`/api/episodes/${params.id}/clean-read`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            context: {
              transcript: content.content.content.transcript,
              metadata: {
                title: content?.content.title,
              },
            },
          }),
        });

        if (!response.ok || !response.body) {
          throw new Error("Failed to generate clean read");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let cleanReadText = "";
        let buffer = "";
        const updateInterval = EPISODE_CONSTANTS.ANIMATION.SUMMARY_UPDATE_INTERVAL;
        let lastUpdateTime = Date.now();

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (buffer.length > 0) {
              cleanReadText += buffer;
            }
            // Parse the final JSON
            try {
              // Remove markdown code block markers if present
              let cleanedText = cleanReadText.trim();
              if (cleanedText.startsWith('```json')) {
                cleanedText = cleanedText.substring(7); // Remove ```json
              }
              if (cleanedText.startsWith('```')) {
                cleanedText = cleanedText.substring(3); // Remove ```
              }
              if (cleanedText.endsWith('```')) {
                cleanedText = cleanedText.substring(0, cleanedText.length - 3); // Remove trailing ```
              }
              cleanedText = cleanedText.trim();
              
              const cleanReadData = JSON.parse(cleanedText);
              setCleanReadData(cleanReadData);
              await saveToRedis("clean-read", cleanReadData, "cleanRead");
            } catch (parseError) {
              console.error("Error parsing clean read JSON:", parseError);
              console.error("Raw text:", cleanReadText);
              setCleanReadData(null);
            }
            break;
          }

          buffer += decoder.decode(value);
          const currentTime = Date.now();

          if (
            currentTime - lastUpdateTime >= updateInterval &&
            buffer.length >= EPISODE_CONSTANTS.ANIMATION.BUFFER_MIN_LENGTH
          ) {
            cleanReadText += buffer;
            // Try to parse partial JSON (for preview purposes)
            try {
              const partialData = JSON.parse(cleanReadText);
              setCleanReadData(partialData);
            } catch {
              // Ignore parse errors for partial data
            }
            buffer = "";
            lastUpdateTime = currentTime;
          }
        }
      } catch (error) {
        console.error("Error generating clean read:", error);
        setCleanReadData(null);
      } finally {
        setIsCleanReadLoading(false);
      }
    },
    [content, setCleanReadData, saveToRedis]
  );

  // Handle timestamp click for mindmap, shownotes, vocabulary, etc.
  const handleTimestampClick = useCallback(
    (timestamp: number, endTimestamp?: number) => {
      const hasSegmentEnd =
        typeof endTimestamp === "number" &&
        Number.isFinite(endTimestamp) &&
        endTimestamp > timestamp;

      activeSegmentRef.current = {
        start: timestamp,
        end: hasSegmentEnd ? endTimestamp : null,
      };

      seekTo(timestamp);

      if (ytPlayer && typeof ytPlayer.seekTo === "function") {
        try {
          ytPlayer.seekTo(timestamp, true);
        } catch (error) {
          console.warn("Failed to seek YouTube player", error);
        }

        setTimeout(() => {
          if (typeof ytPlayer.playVideo === "function") {
            ytPlayer.playVideo();
          } else if (typeof ytPlayer.play === "function") {
            ytPlayer.play();
          }
        }, EPISODE_CONSTANTS.ANIMATION.YOUTUBE_SEEK_DELAY);
      }

      play();
    },
    [play, seekTo, ytPlayer]
  );

  // Auto-pause playback when quiz segments reach their end timestamp
  useEffect(() => {
    const segment = activeSegmentRef.current;
    if (segment.start === null || segment.end === null) {
      return;
    }

    const { start, end } = segment;
    const tolerance = 0.2;

    if (youtubeTime < start - 0.5 || youtubeTime > end + 0.5) {
      activeSegmentRef.current = { start: null, end: null };
      return;
    }

    if (!isPlaying) {
      return;
    }

    if (youtubeTime >= end - tolerance) {
      pause();

      if (ytPlayer) {
        if (typeof ytPlayer.pauseVideo === "function") {
          ytPlayer.pauseVideo();
        } else if (typeof ytPlayer.pause === "function") {
          ytPlayer.pause();
        }
      }

      activeSegmentRef.current = { start: null, end: null };
    }
  }, [youtubeTime, isPlaying, pause, ytPlayer]);

  // Save vocabulary to Redis
  const saveVocabularyToRedis = async (vocabularyData: any) => {
    try {
      await saveToRedis("vocabulary", vocabularyData, "vocabulary");
    } catch (error) {
      console.error("Error saving vocabulary:", error);
    }
  };

  const saveVocabularyToRedis_ = saveVocabularyToRedis;

  const handleVocabularyUpdate = useCallback(
    (updatedVocabulary: any) => {
      setVocabulary(updatedVocabulary);
      updateContentField('vocabulary', updatedVocabulary);
    },
    [setVocabulary, updateContentField]
  );
  const generateVocabulary = useCallback(async () => {
    // 🔥 Premium功能检查
    if (!checkPremiumFeature('Vocabulary')) return;
    
    setIsVocabularyLoading(true);
    try {
      if (
        !content ||
        !content.content ||
        !content.content.content ||
        !content.content.content.transcript
      ) {
        console.error("Content not available for vocabulary generation");
        setIsVocabularyLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Authentication required');
        return;
      }
      
      // 🔥 检查是否已有vocabulary内容
      if (content?.content?.content?.vocabulary) {
        console.log(`✨ Episode already has vocabulary, using cached vocabulary`);
        
        // 直接加载现有结果
        const existingVocabulary = content.content.content.vocabulary;
        setVocabulary(existingVocabulary);
        
        // 保存到Redis并更新生成记录
        await saveToRedis("vocabulary", existingVocabulary, "vocabulary");
        setIsVocabularyLoading(false);
        return;
      }

      const response = await fetch(`/api/episodes/${params.id}/vocabulary-gemini`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          text: content.content.content.transcript,
          language: "中文",
          title: content?.content?.title || content?.title || null,
          channel: content?.content?.author || null,
          description: content?.content?.description || null,
          category: content?.content?.category || null,
          tags: Array.isArray(content?.content?.tags) ? content.content.tags : null,
          publishDate: content?.content?.publishDate || null,
          durationSeconds: typeof content?.content?.duration === 'number'
            ? content.content.duration
            : (typeof youtubeDuration === 'number' ? youtubeDuration : null),
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error("Failed to generate vocabulary");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let vocabularyText = "";
      let buffer = "";
      const updateInterval = EPISODE_CONSTANTS.ANIMATION.SUMMARY_UPDATE_INTERVAL;
      let lastUpdateTime = Date.now();

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.length > 0) {
            vocabularyText += buffer;
          }
          
          // 解析最终的JSON
          try {
            // 清理markdown代码块标记
            let cleanedText = vocabularyText.trim();
            if (cleanedText.startsWith('```json')) {
              cleanedText = cleanedText.substring(7);
            } else if (cleanedText.startsWith('```')) {
              cleanedText = cleanedText.substring(3);
            }
            if (cleanedText.endsWith('```')) {
              cleanedText = cleanedText.substring(0, cleanedText.length - 3);
            }
            cleanedText = cleanedText.trim();
            
            const vocabularyData = JSON.parse(cleanedText);
            setVocabulary(vocabularyData);
            await saveVocabularyToRedis_(vocabularyData);
            
            if (content) {
              const updatedContent = {
                ...content,
                content: {
                  ...content.content,
                  content: {
                    ...content.content.content,
                    vocabulary: vocabularyData,
                  },
                },
              };
              setContent(updatedContent);
            }
          } catch (parseError) {
            console.error("Error parsing vocabulary JSON:", parseError);
            console.error("Raw text:", vocabularyText);
            setVocabulary(null);
          }
          break;
        }

        buffer += decoder.decode(value);
        const currentTime = Date.now();

        if (
          currentTime - lastUpdateTime >= updateInterval &&
          buffer.length >= EPISODE_CONSTANTS.ANIMATION.BUFFER_MIN_LENGTH
        ) {
          vocabularyText += buffer;
          // 可以在这里显示部分生成的内容（可选）
          buffer = "";
          lastUpdateTime = currentTime;
        }
      }
    } catch (error) {
      console.error("Error generating vocabulary:", error);
      setVocabulary(null);
    } finally {
      setIsVocabularyLoading(false);
    }
  }, [content, params.id, setVocabulary, setContent, saveVocabularyToRedis_]);

  // Get vocabulary terms
  const getVocabularyTerms = useCallback(() => {
    
    if (practiceModeType === EPISODE_CONSTANTS.PRACTICE_MODES.VOCABULARY) {
      // For vocabulary mode, use generated vocabulary
      if (!vocabulary || !vocabulary.scenarios) {
        return [];
      }

    const terms = vocabulary.scenarios.flatMap((scenario: any) =>
      scenario.terms.map((term: any) => term.term.toLowerCase())
    );

    return terms;
    }

    if (practiceModeType === EPISODE_CONSTANTS.PRACTICE_MODES.TOEFL) {
      // For TOEFL mode, we'll rely on the GroupedTranscriptEntry component
      // to load TOEFL words dynamically using vocabularyLevelManager
      return ["toefl_dynamic"]; // Return a placeholder to indicate TOEFL mode is active
    }

    return [];
  }, [vocabulary, practiceModeType]);

  // Scroll to current transcript
  // const scrollToCurrentTranscript = useCallback(() => {
  //   const activeTranscript = document.querySelector(
  //     '.transcript-content-container [data-active="true"]'
  //   );
  //   if (activeTranscript) {
  //     const elementTop = activeTranscript.getBoundingClientRect().top;
  //     const offsetTop = window.pageYOffset + elementTop - EPISODE_CONSTANTS.STICKY_HEADERS_HEIGHT;

  //     window.scrollTo({
  //       top: offsetTop,
  //       behavior: "smooth",
  //     });
  //   }
  // }, []);

  // 优化页面卸载清理 - 只在真正离开页面时清理
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 只清理数据缓存，保留词汇检查器供用户继续使用
      clearDataCache();
      
      // 清理词汇缓存
      try {
        import('@/lib/vocabulary-cache').then(({ vocabularyCache }) => {
          vocabularyCache.clear();
        });
      } catch {
        // 忽略导入错误
      }
    };

    // 只在页面真正卸载时清理，避免影响用户操作
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // 组件卸载时不执行完整清理，避免影响功能
      clearDataCache();
    };
  }, [clearDataCache]);

  // Handle tab change with proper typing
  const handleTabChange = useCallback((tab: string) => {
    // Ensure the tab is a valid TabType
    const validTabs = Object.values(EPISODE_CONSTANTS.TABS);
    if (validTabs.includes(tab as TabType)) {
      setActiveTab(tab as TabType);
      
      if (
        typeof window !== "undefined" &&
        (tab === EPISODE_CONSTANTS.TABS.VOCABULARY || tab === EPISODE_CONSTANTS.TABS.PHRASE)
      ) {
        window.requestAnimationFrame(() => {
          const container = contentContainerRef.current;
          const containerTop = container
            ? window.scrollY + container.getBoundingClientRect().top
            : 0;
          const targetTop = Math.max(
            containerTop - EPISODE_CONSTANTS.STICKY_HEADERS_HEIGHT,
            0
          );

          window.scrollTo({
            top: targetTop,
            behavior: "smooth",
          });
        });
      }
      
      // Auto-minimize video when CleanRead tab is selected
      if (
        (tab === EPISODE_CONSTANTS.TABS.CLEANREAD ||
          ((tab === EPISODE_CONSTANTS.TABS.VOCABULARY || tab === EPISODE_CONSTANTS.TABS.PHRASE) && isMobileViewport)) &&
        !isVideoMinimized
      ) {
        setIsVideoMinimized(true);
      }
    }
  }, [isVideoMinimized, isMobileViewport, contentContainerRef]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600">Failed to load episode content.</p>
        </div>
      </div>
    );
  }

      return (
      <AuthGuard>
        <div className={`bg-[#Ff4f5fc] ${isImmersiveMode ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
        {/* Add style tag for no-scrollbar and mobile optimization */}
        <style jsx global>{`
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;  /* Firefox */
          }
          .scrollbar-hide {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none; /* Chrome, Safari and Opera */
          }
          /* Optimize touch scrolling for mobile tabs */
          @media (max-width: 640px) {
            .touch-pan-x {
              touch-action: pan-x;
              -webkit-overflow-scrolling: touch;
            }
            /* Prevent elastic scrolling beyond content */
            .overscroll-x-contain {
              overscroll-behavior-x: contain;
            }
          }
        `}</style>

        {/* Episode Header */}
        <EpisodeHeader
          title={content?.content?.title || content?.title || "Loading..."}
          // titleTranslation={content?.content?.titleTranslation || content?.titleTranslation || ""}
          // preferChineseTitles={Boolean(userSettings?.preferChineseTitles)}
          onLogin={handleLogin}
        />


        {/* Video Player Section - 移除包装容器以确保 sticky 正常工作 */}
        <VideoPlayerSection
          description={cleanReadData?.videoInfo?.description || ""}
          content={content}
          youtubeDuration={youtubeDuration}
          currentTime={youtubeTime} // 新增：同步当前时间到视频播放位置
          isPlaying={isPlaying}
          isLoading={isUserLoading || loadingProgress.data}
          isStarred={isFavorited(episodeShareId)}
          isMinimized={isVideoMinimized}
          isImmersiveMode={isImmersiveMode}
          activeSubtitleText={activeSubtitleText}
          activeSubtitleTranslation={activeSubtitleTranslation}
          activeSubtitleId={activeSubtitleId}
          activeSubtitleTimestamp={activeSubtitleTimestamp}
          activeVocabularyTerms={activeVocabularyTerms}
          onYouTubeReady={handleYouTubeReady}
          onTimeUpdate={handleTimeUpdate}
          onDurationChange={handleDurationChange}
          onPlayPause={(videoIsPlaying) => handleVideoPlayerStateChange(videoIsPlaying)}
          onEnded={handleMediaEnded}
          onStarClick={handleStarClick}
          onAddToListClick={handleAddToListClick}
          onShareClick={handleShareClick}
          onToggleMinimize={handleToggleMinimize}
          onImmersiveModeChange={handleImmersiveModeChange}
          onMobileImmersiveModeChange={handleMobileImmersiveModeChange}
          contentTabsActiveTab={activeTab}
          onContentTabChange={handleTabChange}
          showSubtitlesInImmersive={showSubtitlesInImmersive}
          showTranslationInImmersive={showTranslationInImmersive}
          onShowSubtitlesInImmersiveChange={setShowSubtitlesInImmersive}
          onRestoreActiveSubtitle={restoreActiveSubtitle}
          formatDuration={formatDuration}
        />

        {/* Content Container - Always render, but adjust styling for mobile immersive mode */}
        {(
          <div
            ref={contentContainerRef}
            className={`mx-auto pt-0.5 sm:pt-0 transition-all duration-300 ease-in-out ${
              mobileImmersiveMode 
                ? "px-0 max-w-none w-full" // Mobile immersive mode: no constraints
                : `px-0 pt-0 sm:px-6 sm:pb-14 pb-0 ${
                    (() => {
                      if (!mobileImmersiveMode && isImmersiveMode) {
                        return "max-w-7xl";
                      }

                      switch (activeTab) {
                        case EPISODE_CONSTANTS.TABS.MINDMAP:
                          return EPISODE_CONSTANTS.CONTENT_MAX_WIDTH.MINDMAP;
                        case EPISODE_CONSTANTS.TABS.VOCABULARY:
                        case EPISODE_CONSTANTS.TABS.PHRASE:
                        case EPISODE_CONSTANTS.TABS.CLEANREAD:
                          return EPISODE_CONSTANTS.CONTENT_MAX_WIDTH.VOCABULARY;
                        case EPISODE_CONSTANTS.TABS.SUMMARY:
                          return summaryShowTranslation
                            ? EPISODE_CONSTANTS.CONTENT_MAX_WIDTH.SUMMARY_WITH_TRANSLATION
                            : EPISODE_CONSTANTS.CONTENT_MAX_WIDTH.SUMMARY;
                        default:
                          return EPISODE_CONSTANTS.CONTENT_MAX_WIDTH.DEFAULT;
                      }
                    })()
                  }`
            }`}
          >
          {/* Fixed floating buttons for Transcript tab */}
          {activeTab === EPISODE_CONSTANTS.TABS.TRANSCRIPT && !isImmersiveMode && !mobileImmersiveMode && (
            <FloatingActionButtons
              showTranslation={showTranslation}
              setShowTranslation={setShowTranslation}
              wordHighlightEnabled={wordHighlightEnabled}
              setWordHighlightEnabled={setWordHighlightEnabled}
              wordHighlightLoading={wordHighlightLoading}
              setWordHighlightLoading={setWordHighlightLoading}
              practiceMode={practiceMode}
              setPracticeMode={setPracticeMode}
              practiceModeType={practiceModeType}
              setPracticeModeType={setPracticeModeType}
              vocabWarmUpOpen={vocabWarmUpOpen}
              setVocabWarmUpOpen={setVocabWarmUpOpen}
              vocabulary={vocabulary}
              setIsWordCheckerReady={setIsWordCheckerReady}
              userSettings={userSettings}
              transcript={groupedSentences}
              episodeId={params.id}
              onTranscriptUpdate={handleTranscriptUpdate}
              onVocabularyUpdate={handleVocabularyUpdate}
            />
          )}

          {/* Immersive Floating Action Buttons - Desktop immersive mode */}
          {isImmersiveMode && !mobileImmersiveMode && (
            <ImmersiveFloatingActionButtons
              showSubtitles={showSubtitlesInImmersive}
              setShowSubtitles={setShowSubtitlesInImmersive}
              showTranslation={showTranslationInImmersive}
              setShowTranslation={setShowTranslationInImmersive}
              allTranslationsRevealed={revealAllTranslationsInImmersive}
              setAllTranslationsRevealed={setRevealAllTranslationsInImmersive}
              onExitImmersive={() => setIsImmersiveMode(false)}
            />
          )}

          {/* Mobile Immersive Floating Buttons - Mobile immersive mode */}
          {mobileImmersiveMode && (
            <MobileImmersiveFloatingButtons
              showSubtitles={showSubtitlesInImmersive}
              setShowSubtitles={setShowSubtitlesInImmersive}
              showTranslation={showTranslationInImmersive}
              setShowTranslation={setShowTranslationInImmersive}
              allTranslationsRevealed={revealAllTranslationsInImmersive}
              setAllTranslationsRevealed={setRevealAllTranslationsInImmersive}
              sentence={activeSubtitleText}
              translation={activeSubtitleTranslation}
              subtitleTimestamp={activeSubtitleTimestamp || undefined}
              currentTime={youtubeTime}
              episodeId={params.id}
              episodeTitle={content?.content?.title || content?.title}
              episodeThumbnail={getThumbnailUrl()}
              activeSubtitleId={activeSubtitleId}
              userSettings={userSettings}
              session={session}
              activeVocabularyTerms={activeVocabularyTermLabels}
              vocabulary={vocabulary}
              onVocabularyUpdate={handleVocabularyUpdate}
              onExitImmersive={() => setMobileImmersiveMode(false)}
            />
          )}

          {(loadingProgress.data || isUserLoading) && !mobileImmersiveMode && (
            <TranscriptSectionSkeleton />
          )}

          {mobileImmersiveMode && !loadingProgress.data && !isUserLoading && (
            <MobileImmersiveContent
              vocabulary={vocabulary}
              currentTime={youtubeTime}
              onTimestampClick={handleTimestampClick}
              onActiveVocabularyTermsChange={handleActiveVocabularyTermsChange}
              showTranslation={showTranslationInImmersive}
              revealAllTranslations={revealAllTranslationsInImmersive}
              showSubtitles={showSubtitlesInImmersive}
              groupedSentences={groupedSentences}
              ytPlayer={ytPlayer}
              userSettings={userSettings}
              wordHighlightEnabled={wordHighlightEnabled}
              practiceMode={practiceMode}
              practiceModeType={practiceModeType}
              vocabularyTerms={getVocabularyTerms()}
              setIsPlaying={setIsPlaying}
              isWordCheckerReady={isWordCheckerReady}
              episodeId={params.id}
              activeSubtitleId={activeSubtitleId}
              onActiveSubtitleChange={handleActiveSubtitleChange}
              activeSubtitleText={activeSubtitleText}
              activeSubtitleTranslation={activeSubtitleTranslation}
              activeVocabularyTerms={activeVocabularyTerms}
              hiddenVocabularyTermKeys={hiddenVocabularyTermKeys}
              session={session}
              hasTwoBrokeGirlsAccess={hasTwoBrokeGirlsAccess}
              isTwoBrokeGirlsEpisode={isTwoBrokeGirlsEpisode}
              onTranscriptUpdate={handleTranscriptUpdate}
            />
          )}

          {/* Content area */}
          {!loadingProgress.data && !isUserLoading && !mobileImmersiveMode && (
            <ContentRenderer
              activeTab={activeTab}
              user={user}
              session={session}
              onLogin={handleLogin}
              
              // Summary props
              summary={summary}
              isSummaryLoading={isSummaryLoading}
              onGenerateSummary={() => {
                if (checkTranscriptExists()) {
                  return generateSummary(content.content.content.transcript);
                }
              }}
              onSummaryTranslationStateChange={handleSummaryTranslationStateChange}
              
              // Mindmap props
              mindmapData={mindmapData}
              content={content}
              isLoading={loadingProgress.data || isMindmapLoading}
              onGenerateMindmap={() => {
                if (checkTranscriptExists()) {
                  return generateMindmap();
                }
              }}
              onTimestampClick={handleTimestampClick}
              
              // Transcript props
              groupedSentences={groupedSentences}
              youtubeTime={youtubeTime}
              ytPlayer={ytPlayer}
              userSettings={userSettings}
              wordHighlightEnabled={wordHighlightEnabled}
              practiceMode={practiceMode}
              practiceModeType={practiceModeType}
              vocabularyTerms={getVocabularyTerms()}
              setIsPlaying={setIsPlaying}
              showTranslation={showTranslation}
              isWordCheckerReady={isWordCheckerReady}
              isVideoMinimized={isVideoMinimized}
              episodeDuration={youtubeDuration}
              
              // Vocabulary props
              vocabulary={vocabulary}
              object={null}
              isVocabularyLoading={isVocabularyLoading}
              onGenerateVocabulary={() => {
                if (checkTranscriptExists()) {
                  return generateVocabulary();
                }
              }}
              onVocabularyUpdate={(enhancedVocabulary) => {
                setVocabulary(enhancedVocabulary);
                // Also update content state for persistence
                if (content) {
                  const updatedContent = {
                    ...content,
                    content: {
                      ...content.content,
                      content: {
                        ...content.content.content,
                        vocabulary: enhancedVocabulary,
                      },
                    },
                  };
                  setContent(updatedContent);
                }
              }}
              onVocabularyCardSwipe={handleVocabularyCardSwipe}
              hiddenVocabularyTermKeys={hiddenVocabularyTermKeys}
              onFilterHiddenTermsChange={handleFilterHiddenTermsChange}
              
              // Clean Read props
              cleanReadData={cleanReadData}
              isCleanReadLoading={isCleanReadLoading}
              onGenerateCleanRead={() => {
                if (checkTranscriptExists()) {
                  return generateCleanRead();
                }
              }}
              
              // Focus Mode props
              isFocusMode={isFocusMode}
              onFocusModeChange={handleFocusModeChange}
              
              // Immersive Mode and Subtitle props
              isImmersiveMode={isImmersiveMode}
              activeSubtitleId={activeSubtitleId}
              onActiveSubtitleChange={handleActiveSubtitleChange}
              onActiveVocabularyTermsChange={handleActiveVocabularyTermsChange}
              showTranslationInImmersive={showTranslationInImmersive}
              revealAllTranslationsInImmersive={revealAllTranslationsInImmersive}
              
              // Learning Sidebar props (AI generation will be handled by ContentRenderer)
              learningSidebarData={learningSidebarData}
              onGenerateLearningSidebar={undefined}
              
              // Data persistence props
              episodeId={params.id}
              episodeData={content}
              saveToRedis={saveToRedis}
              accessInfo={accessInfo}
              generationRecords={generationRecords}
              hasTwoBrokeGirlsAccess={hasTwoBrokeGirlsAccess}
              allowBasicPlanLite={allowBasicPlanLite}
              
              // Transcript update callback
              onTranscriptUpdate={handleTranscriptUpdate}
            />
          )}
          </div>
        )}

        {/* Bottom Player Controls 现在由全局播放器提供 */}

        {/* Dialogs */}
        <VocabWarmUpDialog
          open={vocabWarmUpOpen}
          onClose={() => setVocabWarmUpOpen(false)}
          vocabulary={vocabulary}
        />

        <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
        
        <AddToPlaylistDialog
          episodeId={content?.shareId || content?.content?.shareId || ''}
          episodeTitle={content?.content?.title}
          isOpen={showAddToPlaylist}
          onClose={() => setShowAddToPlaylist(false)}
        />
        
        <ExportEpisodeDialog
          content={content}
          cleanReadData={cleanReadData}
          isOpen={showExportDialog}
          onClose={() => setShowExportDialog(false)}
        />
        
        <FeatureLimitDialog
          isOpen={showPremiumPrompt}
          onClose={() => setShowPremiumPrompt(false)}
          feature={premiumPromptFeature}
          type="premium_required"
        />
      </div>
    </AuthGuard>
  );
}
